import React, { useEffect, useState } from 'react';
import * as debounce from 'lodash.debounce';
import { translate } from '../../Services/translation';
import { useDispatch, useSelector } from 'react-redux';
import { toggle, update } from '../../Actions/Cart.action';
import QuantityInput from '../QuantityInput';
import SideMenu from '../SideMenu';
import Icon from '../IconBase/Icon';
import MessageBar from '../MessageBar/MessageBar';
import CartProducts from '../Cart/CartProducts';
import { dataLayerGtmViewCart } from '../../Services/Gtm.service';
import(`./styles/${window.__litium.themeName}.scss`);

const MiniCart = () => {
  const dispatch = useDispatch();
  const {
    quantity,
    orderRows,
    fees,
    grandTotal,
    orderTotal,
    gtmGrandTotal,
    vat,
    checkoutUrl,
    showInfo,
    addToCartAnimating = false,
    discount,
    showMiniCartGrandTotal,
    miniCartGrandTotal,
    earliestDelivery = null,
    leftToFreeShipping = null,
    freeShipping = null,
    isForConsumer,
    b2B,
  } = useSelector((state) => state.cart);

  const cart = {
    quantity: quantity,
    orderRows: orderRows,
    fees: fees,
    grandTotal: grandTotal,
    vat: vat,
    checkoutUrl: checkoutUrl,
    showInfo: showInfo,
    addToCartAnimating: addToCartAnimating,
    discount: discount,
    showMiniCartGrandTotal: showMiniCartGrandTotal,
    miniCartGrandTotal: miniCartGrandTotal,
    earliestDelivery: earliestDelivery,
    leftToFreeShipping: leftToFreeShipping,
    freeShipping: freeShipping,
    isForConsumer: isForConsumer,
  };

  const formatDate = (input) => {
    return input.toJSON().slice(0, 10);
  };

  const currentDate = formatDate(new Date());

  const onToggle = () => {
    dispatch(toggle());
  };
  const onClose = () => {
    dispatch(toggle(false));
  };

  const [abortController, setAbortController] = useState();

  // Must be set after client initialized
  useEffect(() => setAbortController(new AbortController()), []);

  const updateOrderRowQuantity = debounce(
    (articleNumber, quantity, rowSystemId) =>
      dispatch(update(articleNumber, quantity, rowSystemId, abortController)),
    200
  );

  const hasOrderRows = orderRows && Object.keys(orderRows).length > 0;

  const footer = (
    <>
      <div className="cart__sidemenu-footer-row">
        <h5>
          {b2B
            ? translate('checkout.order.total')
            : translate('checkout.order.totalwithvat')}
        </h5>
        <div className="cart__sidemenu-footer-row-section">
          <h5>{orderTotal}</h5>
          <span className="side-menu__footer-info">
            {translate(
              cart.freeShipping
                ? 'checkout.order.freeshipping'
                : 'checkout.order.shippinginfo'
            )}
          </span>
          {currentDate <= earliestDelivery ? (
            <span className="side-menu__footer-info">
              {translate('minicart.shiplater')}: {earliestDelivery}
            </span>
          ) : (
            <span className="side-menu__footer-info">
              {translate('minicart.sentimmidiately')}
            </span>
          )}
        </div>
      </div>
      <div className="cart__sidemenu-footer-row">
        <a href={checkoutUrl} className="btn btn--secondary">
          {translate('minicart.checkout')}
        </a>
      </div>
      <div className="cart__sidemenu-footer-row">
        <ul>
          <li>
            <Icon iconCode="local_shipping" />
            {translate('minicart.deliveryusp')}
          </li>
          <li>
            <Icon iconCode="credit_card" />
            {translate('minicart.paymentusp')}
          </li>
          <li>
            <Icon iconCode="new_releases" />
            {translate('minicart.warrantyusp')}
          </li>
        </ul>
      </div>
    </>
  );

  const OrderRowItem = (row) => {
    const onQuantityChange = (value) => {
      updateOrderRowQuantity(row.articleNumber, value, row.rowSystemId);
    };

    return (
      <li key={row.rowSystemId} className="cart__sidemenu-orderrow">
        <div className="cart__sidemenu-orderrow-wrapper">
          <div className="cart__sidemenu-orderrow-images">
            {row.brandLogoUrl ? (
              <img src={row.brandLogoUrl} loading="lazy" />
            ) : null}
            <a href={row.url}>
              <img src={row.image} loading="lazy" />
            </a>
          </div>
          <div className="cart__sidemenu-orderrow-information-container">
            <div className="cart__sidemenu-orderrow-information">
              <div className="cart__sidemenu-orderrow-text">
                <span className="product__info--art-nr">
                  <span>{row.articleNumber}</span>
                </span>
                <h3 className="cart__sidemenu-orderrow-header">
                  <a className="color-grey-900" href={row.url}>
                    {row.brandName} {row.baseProductName}
                  </a>
                </h3>
                <span>{row.name}</span>
              </div>
              <div className="cart__sidemenu-orderrow-price">
                <span className="cart__sidemenu-orderrow-mainprice">
                  {row.totalPrice}
                </span>
                <span className="cart__sidemenu-orderrow-subprice">
                  {row.unitPrice}
                </span>
              </div>
            </div>
            <div className="cart__sidemenu-orderrow-information-subcontainer show-for-large">
              <div className="cart__sidemenu-orderrow-information cart__sidemenu-orderrow-information--footer">
                <div className="in-stock-tag">
                  <Icon iconCode="package" />
                  <span>{row.stockDescription}</span>
                </div>
                <div className="cart__sidemenu-orderrow-quantity">
                  <QuantityInput
                    disabled={!row.canEdit}
                    value={row.quantity}
                    id={row.rowSystemId}
                    onQuantityIncrease={() =>
                      onQuantityChange(parseInt(row.quantity) + 1)
                    }
                    onQuantityDecrease={() =>
                      onQuantityChange(parseInt(row.quantity) - 1)
                    }
                    onChange={(quantity) => onQuantityChange(quantity)}
                  />
                </div>
                {row.canEdit && (
                  <div className="cart__sidemenu-orderrow-remove"></div>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="cart__sidemenu-orderrow-information-subcontainer hide-for-large ">
          <div className="cart__sidemenu-orderrow-information cart__sidemenu-orderrow-information--footer">
            <div className="in-stock-tag">
              <Icon iconCode="package" />
              <span>{row.stockDescription}</span>
            </div>
            <div className="cart__sidemenu-orderrow-quantity">
              <QuantityInput
                disabled={!row.canEdit}
                value={row.quantity}
                id={row.rowSystemId}
                onQuantityIncrease={() =>
                  onQuantityChange(parseInt(row.quantity) + 1)
                }
                onQuantityDecrease={() =>
                  onQuantityChange(parseInt(row.quantity) - 1)
                }
                onChange={(quantity) => onQuantityChange(quantity)}
              />
            </div>
            {row.canEdit && (
              <div className="cart__sidemenu-orderrow-remove">
                <Icon iconCode="trash" onClick={() => onQuantityChange(0)} />
              </div>
            )}
          </div>
        </div>
      </li>
    );
  };

  const orderRowItems = hasOrderRows
    ? orderRows?.map((row) => <OrderRowItem key={row.rowSystemId} {...row} />)
    : null;

  const headerRef = (
    <div className="cart__sidemenu">
      <div className="row cart cart--mini cart__sidemenu-header">
        <h5>{translate('checkout.cart.title')}</h5>
        <span className="cart__title">
          {orderRowItems ? '(' + orderRowItems.length + ')' : '(0)'}
        </span>
      </div>
    </div>
  );

  const messageItem = !isForConsumer ? (
    leftToFreeShipping ? (
      <MessageBar
        text={translate('checkout.order.messagebar').replace(
          '{0}',
          leftToFreeShipping
        )}
        type={'error'}
        icon="local_shipping"
      />
    ) : null
  ) : (
    <MessageBar
      text={
        translate('checkout.order.messagebar') !== 'checkout.order.messagebar'
          ? translate('checkout.order.messagebar')
          : undefined
      }
      type={'info'}
      icon="local_shipping"
    />
  );

  const onClickShowMiniCart = () => {
    onToggle();

    try {
      let gtmOrderRows = [];
      orderRows.map((row) => {
        const gtmOrderRowIndex = gtmOrderRows.findIndex(
          (e) => e.articleNumber === row.articleNumber
        );
        if (gtmOrderRowIndex === -1) {
          row.gtmEcommerceItem.quantity = row.quantity;
          row.gtmEcommerceItem.index = gtmOrderRows.length;
          gtmOrderRows.push(row);
        } else {
          gtmOrderRows[gtmOrderRowIndex].gtmEcommerceItem.quantity +=
            row.quantity;
        }
      });

      if (gtmOrderRows.length > 0) {
        dataLayerGtmViewCart({
          currency: gtmOrderRows[0].gtmEcommerceItem.currency,
          gtmGrandTotal: gtmGrandTotal,
          orderRows: gtmOrderRows,
        });
      }
    } catch (err) {
      console.log(err);
    }
  };

  return (
    <div
      className={`cart cart--mini ${addToCartAnimating ? 'cart--animate' : ''}`}
    >
      <a className="cart__button" onClick={() => onClickShowMiniCart()}>
        <div className="cart__icon">
          <span className="cart__quantity">{quantity}</span>
          <Icon
            iconCode={
              window.__litium.themeName == 'WTG'
                ? 'shopping_cart'
                : window.__litium.themeName == 'ALF'
                ? 'local_mall'
                : 'shopping_bag'
            }
          />
        </div>
        <span className="cart__title show-for-large">
          {showMiniCartGrandTotal && <div>{miniCartGrandTotal}</div>}
        </span>
      </a>
      <SideMenu
        id="cart"
        className="side-menu--cart"
        open={showInfo}
        headerRef={headerRef}
        messageBar={messageItem}
        onClosed={() => onClose(false)}
        footer={hasOrderRows ? footer : null}
      >
        <div className="cart__sidemenu-content">
          {hasOrderRows ? (
            <>
              <CartProducts
                cart={cart}
                mobile={true}
                type="minicart"
                b2c={isForConsumer}
              />
            </>
          ) : (
            <div className="cart__sidemenu-empty">
              <h4>{translate('checkout.cart.empty')}</h4>
            </div>
          )}
        </div>
      </SideMenu>
    </div>
  );
};

export default MiniCart;
